import type { User, UserStatus } from '@repo-level/core-front';
import type { BonanzaPromoApi } from 'api/cms/bonanzaPromo';
import type { PlayerBonus } from 'api/common';
import moment from 'moment';
import { CurrencyCode } from './currency';
import { isPromoAvailableDate } from './isDateRange';
import { bonusRestrictedGroups, groupMember, someGroupMember } from './user';

enum BonanzaTier {
  Casual = 'bonanza_casual_july',
  Gold = 'bonanza_gold_july',
  PlatinumDiamond = 'bonanza_platinum_diamond_july',
}

export enum BonanzaStatus {
  LEVEL0 = '0%',
  LEVEL1 = '25%',
  LEVEL2 = '50%',
  LEVEL3 = '75%',
  LEVEL4 = '100%',
  // tech status, not a user group
  BONUS_ISSUED = 'Bonus_Issued',
  PROMO_LOCKED = 'Used_Bonus',
  UNLOGGED = 'UNLOGGED',
}

export const BONANZA_STORAGE = 'bonanza';
export const BONANZA_STARTED_STORAGE = 'bonanza_started';

const BONANZA_GROUP_REGEX =
  /^(bonanza_casual_july|bonanza_gold_july|bonanza_platinum_diamond_july)_\d+%$/i;
const BONANZA_BONUS_PREFIX = 'Elven';
export const BONANZA_ITERATION_DURATION_DAYS = 7;
const BONANZA_FULL_DATE_FORMAT: Partial<Record<string, string>> = {
  de: 'ddd',
};

export const BONANZA_ACTIVE_PROGRESS_STATUSES = [
  BonanzaStatus.LEVEL0,
  BonanzaStatus.LEVEL1,
  BonanzaStatus.LEVEL2,
  BonanzaStatus.LEVEL3,
  BonanzaStatus.LEVEL4,
] as const;

const extractStatusPercent = (statusId: string): string =>
  statusId.split('_').slice(-1)[0];

const getHighestBonusGroup = (bonusGroups: UserStatus[]): UserStatus =>
  bonusGroups.reduce((max, current) =>
    Number.parseInt(extractStatusPercent(current.id), 10) >
    Number.parseInt(extractStatusPercent(max.id), 10)
      ? current
      : max
  );

export const getUserBonanzaGroup = (
  promoPrefix: string,
  statuses?: UserStatus[]
): BonanzaStatus => {
  if (!statuses) return BonanzaStatus.UNLOGGED;

  const bonusGroups = statuses.filter(
    ({ id }) => id.toLowerCase().includes(promoPrefix) && id.includes('%')
  );

  if (!bonusGroups.length) return BonanzaStatus.LEVEL0;

  localStorage.removeItem(BONANZA_STARTED_STORAGE);

  const bonusGroupPercent = extractStatusPercent(
    getHighestBonusGroup(bonusGroups).id
  ) as BonanzaStatus;

  const isLevel4 = bonusGroupPercent === BonanzaStatus.LEVEL4;
  const isHitTheBonanzaStorage = localStorage.getItem(BONANZA_STORAGE);

  if (isLevel4 && isHitTheBonanzaStorage) return BonanzaStatus.PROMO_LOCKED;
  if (isLevel4 && !isHitTheBonanzaStorage) return BonanzaStatus.BONUS_ISSUED;
  if (!isLevel4 && isHitTheBonanzaStorage)
    localStorage.removeItem(BONANZA_STORAGE);

  return bonusGroupPercent;
};

export const bonanzaPromoProgressPercent: Record<BonanzaStatus, number> = {
  [BonanzaStatus.UNLOGGED]: 0,
  [BonanzaStatus.LEVEL0]: 0,
  [BonanzaStatus.LEVEL1]: 25,
  [BonanzaStatus.LEVEL2]: 50,
  [BonanzaStatus.LEVEL3]: 75,
  [BonanzaStatus.LEVEL4]: 100,
  [BonanzaStatus.BONUS_ISSUED]: 100,
  [BonanzaStatus.PROMO_LOCKED]: 100,
};

const bonanzaSupportedCurrencies: string[] = [
  CurrencyCode.AUD,
  CurrencyCode.CAD,
  CurrencyCode.NZD,
  CurrencyCode.NOK,
  CurrencyCode.EUR,
  CurrencyCode.USD,
];

const bonanzaRewardMap: Record<
  BonanzaTier,
  Partial<Record<CurrencyCode, number>>
> = {
  [BonanzaTier.Casual]: {
    [CurrencyCode.AUD]: 25,
    [CurrencyCode.CAD]: 25,
    [CurrencyCode.NZD]: 25,
    [CurrencyCode.NOK]: 250,
    [CurrencyCode.EUR]: 25,
    [CurrencyCode.USD]: 25,
  },
  [BonanzaTier.Gold]: {
    [CurrencyCode.AUD]: 35,
    [CurrencyCode.CAD]: 35,
    [CurrencyCode.NZD]: 35,
    [CurrencyCode.NOK]: 350,
    [CurrencyCode.EUR]: 35,
    [CurrencyCode.USD]: 35,
  },
  [BonanzaTier.PlatinumDiamond]: {
    [CurrencyCode.AUD]: 50,
    [CurrencyCode.CAD]: 50,
    [CurrencyCode.NZD]: 50,
    [CurrencyCode.NOK]: 500,
    [CurrencyCode.EUR]: 50,
    [CurrencyCode.USD]: 50,
  },
};

const findBonanzaGroup = (user?: User): BonanzaTier | undefined => {
  if (!user?.statuses) return;

  const bonanzaRegex = new RegExp(BONANZA_GROUP_REGEX, 'i');
  const matches = user.statuses.filter(({ id }) => bonanzaRegex.test(id));

  if (!matches.length) return;

  const matched = matches
    .reduce((highest, current) => {
      const highestPercent = Number.parseInt(
        highest.id.split('_').slice(-1)[0],
        10
      );
      const currentPercent = Number.parseInt(
        current.id.split('_').slice(-1)[0],
        10
      );
      return currentPercent > highestPercent ? current : highest;
    })
    .id.toLowerCase();

  if (matched.startsWith(BonanzaTier.PlatinumDiamond))
    return BonanzaTier.PlatinumDiamond;
  if (matched.startsWith(BonanzaTier.Gold)) return BonanzaTier.Gold;
  return BonanzaTier.Casual;
};

export const getUserReward = (user?: User) => {
  const userCurrency = user?.currency ?? CurrencyCode.USD;
  const currency = bonanzaSupportedCurrencies.includes(userCurrency)
    ? (userCurrency as CurrencyCode)
    : CurrencyCode.USD;

  const userGroupId = findBonanzaGroup(user) ?? BonanzaTier.Casual;

  const reward = bonanzaRewardMap[userGroupId]?.[currency] ?? 0;

  return { reward, currency };
};

export const getBonanzaBonus = (bonuses: PlayerBonus[]) =>
  bonuses.find(({ title }) => title.includes(BONANZA_BONUS_PREFIX));

export const getCurrentBonanzaIterationDays = (
  startDate: string,
  endDate: string,
  locale: string
): {
  startDay: string;
  endDay: string;
  endDayFull: string;
  nextStartDay: string;
  isLastIteration: boolean;
} => {
  const promoStart = moment.utc(startDate);
  const promoEnd = moment.utc(endDate);
  const today = moment.utc();
  const daysSinceStart = Math.max(0, today.diff(promoStart, 'days'));
  const currentIteration = Math.floor(
    daysSinceStart / BONANZA_ITERATION_DURATION_DAYS
  );
  const totalIterations = Math.ceil(
    promoEnd.diff(promoStart, 'days') / BONANZA_ITERATION_DURATION_DAYS
  );
  const isLastIteration = currentIteration + 1 >= totalIterations;

  const momentLocale = locale.split('-')[0];
  const fullDateFormat = BONANZA_FULL_DATE_FORMAT[momentLocale] ?? 'ddd';

  const currentIterationStart = promoStart
    .clone()
    .add(currentIteration * BONANZA_ITERATION_DURATION_DAYS, 'days');
  const currentIterationEnd = currentIterationStart
    .clone()
    .add(BONANZA_ITERATION_DURATION_DAYS - 1, 'days');
  const nextIterationStart = currentIterationStart
    .clone()
    .add(BONANZA_ITERATION_DURATION_DAYS, 'days');

  const stripDots = (str: string) => str.replace(/\./g, '');

  return {
    startDay: stripDots(
      currentIterationStart.locale(momentLocale).format(fullDateFormat)
    ),
    endDay: stripDots(
      currentIterationEnd.locale(momentLocale).format(fullDateFormat)
    ),
    endDayFull: stripDots(
      currentIterationEnd.locale(momentLocale).format(fullDateFormat)
    ),
    nextStartDay: nextIterationStart.locale(momentLocale).format('dddd'),
    isLastIteration,
  };
};

export const checkIsBonanzaPromoAvailable = (
  data: BonanzaPromoApi | null,
  user?: User
) => {
  if (data && data.start && data.end) {
    const end = moment.utc(data.end);
    const now = moment.utc();
    const diffDays = end.diff(now, 'days');
    const isLastWeek = diffDays <= 7;
    const isPromoLocked = groupMember(BonanzaStatus.PROMO_LOCKED)(user);

    return (
      !someGroupMember(bonusRestrictedGroups)(user) &&
      isPromoAvailableDate(data.start, data.end) &&
      !(isPromoLocked && isLastWeek)
    );
  }
  return false;
};
